import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Gift, Copy, Check } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const CHANNEL_INTENTS = {
  whatsapp: (url, freeMinutes) =>
    `https://wa.me/?text=${encodeURIComponent(
      `I just started using LinguaSub for AI subtitles that preserve slang and humour. ${freeMinutes} minutes free — one time for new users: ${url}`
    )}`,
  twitter: (url, freeMinutes) =>
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      `Just signed up for @LinguaSub — AI subtitles that preserve slang and humour. ${freeMinutes} free minutes once for new users:`
    )}&url=${encodeURIComponent(url)}`,
};

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const TwitterIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const buildInviteUrl = (origin, code, channel) =>
  `${origin}/?ref=${encodeURIComponent(code)}${channel ? `&ch=${channel}` : ""}`;

// One-shot post-signup modal. Fires on the first dashboard visit AFTER
// a fresh signup, then never again for that user on that device.
export default function WelcomeModal() {
  const [open, setOpen] = useState(false);
  const [refData, setRefData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const flag = (() => {
      try { return localStorage.getItem("linguasub_show_welcome"); }
      catch { return null; }
    })();
    if (!flag) return;
    try { localStorage.removeItem("linguasub_show_welcome"); } catch { /* noop */ }
    (async () => {
      try {
        const { data } = await api.get("/referrals/me");
        setRefData(data);
        setOpen(true);
      } catch { /* silent */ }
    })();
  }, []);

  const shareVia = useCallback(async (channel) => {
    if (!refData) return;
    const url = buildInviteUrl(window.location.origin, refData.code, channel);
    if (channel === "copy") {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
      }
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2500);
      return;
    }
    const intent = CHANNEL_INTENTS[channel];
    if (!intent) return;
    window.open(intent(url, refData.free_minutes), "_blank", "noopener,noreferrer");
  }, [refData]);

  if (!refData) return null;
  const inviteUrl = buildInviteUrl(window.location.origin, refData.code);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="welcome-modal"
        className="max-w-lg bg-zinc-950 border-zinc-800 text-white rounded-none p-0 gap-0"
      >
        <DialogHeader className="p-6 border-b border-zinc-800">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-2 flex items-center gap-1.5">
            <Gift className="w-3 h-3" /> Welcome to LinguaSub
          </div>
          <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-white">
            Earn {refData.discount_percent}% off by referring friends.
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-400 font-mono leading-relaxed">
            Share your link with {refData.progress_target} distinct friends who sign up. New users get {refData.free_minutes} free minutes once.
            You earn {refData.discount_percent}% off an eligible paid purchase, automatically at checkout. One reward per purchase; no stacking. Referred buyers pay the advertised price.
          </DialogDescription>
        </DialogHeader>
        <div className="p-6 space-y-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Your referral link
          </div>
          <input
            type="text"
            readOnly
            value={inviteUrl}
            onFocus={(e) => e.target.select()}
            data-testid="welcome-link-input"
            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs font-mono px-3 py-2.5 focus:outline-none focus:border-blue-500 select-all"
          />
          {/* Channel-tagged share intents — same URLs as ReferralCard so
              analytics attributes both surfaces to the same buckets. */}
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => shareVia("whatsapp")}
              data-testid="welcome-share-whatsapp"
              className="inline-flex items-center justify-center gap-2 h-11 bg-[#25D366] hover:bg-[#1DA851] text-white text-[10px] font-mono uppercase tracking-widest transition-colors"
            >
              <WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp
            </button>
            <button
              type="button"
              onClick={() => shareVia("twitter")}
              data-testid="welcome-share-twitter"
              className="inline-flex items-center justify-center gap-2 h-11 bg-black border border-zinc-700 hover:bg-zinc-900 text-white text-[10px] font-mono uppercase tracking-widest transition-colors"
            >
              <TwitterIcon className="w-3.5 h-3.5" /> Twitter
            </button>
            <button
              type="button"
              onClick={() => shareVia("copy")}
              data-testid="welcome-share-copy"
              className="inline-flex items-center justify-center gap-2 h-11 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800 text-white text-[10px] font-mono uppercase tracking-widest transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="welcome-dismiss-btn"
            className="w-full text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-white py-2 transition-colors"
          >
            Skip for now
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
